import { Box, VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { Radio, RadioGroup } from '@mangovoice/mango-ui'

import { DialByNameNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useDialByNameNode } from './hooks/useDialByNameNode'

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

export interface DialByNameNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void

  showDeleteButton?: boolean
}

const DialByNameNodeComponent: React.FC<DialByNameNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as DialByNameNodeData & Record<string, unknown>
  const {
    currentLookupId,
    currentSortOrder,
    handleLookupIdChange,
    handleSortOrderChange,
    hasData,
  } = useDialByNameNode(id, nodeData)

  const handleSortOrderSelectChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as
        'first_name' | 'last_name' | 'first_last_name'
      handleSortOrderChange(value)
    },
    [handleSortOrderChange],
  )

  const t = useSwitchboardT()

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.dial_by_name', 'DIAL BY NAME')}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      sourceHandleLabel={text(
        t,
        'switchboard.nodes.dialByName.invalid',
        'Invalid',
      )}
    >
      <VStack align="stretch" gap={3}>
        {/* Lookup By - Radio buttons */}
        <Box>
          <Box fontSize="sm" fontWeight="medium" color="fg.default" mb={2}>
            {text(t, 'switchboard.nodes.dialByName.lookupBy', 'Lookup By')}
          </Box>
          <RadioGroup
            className="nodrag"
            size="sm"
            value={currentLookupId}
            onValueChange={(details) => {
              if (
                details.value === 'extension_name' ||
                details.value === 'username'
              ) {
                handleLookupIdChange(details.value)
              }
            }}
            onMouseDown={stopPropagation}
            onPointerDown={stopPropagation}
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
          >
            <VStack align="stretch" gap={2}>
              <Radio value="extension_name">
                {text(
                  t,
                  'switchboard.nodes.dialByName.extensionName',
                  'Extension Name',
                )}
              </Radio>
              <Radio value="username">
                {text(t, 'switchboard.nodes.dialByName.userName', 'User Name')}
              </Radio>
            </VStack>
          </RadioGroup>
        </Box>

        {/* Sort Order - Select dropdown (only shown when User Name is selected) */}
        {currentLookupId === 'username' && (
          <NodeSelect
            label={text(
              t,
              'switchboard.nodes.dialByName.sortOrder',
              'Sort Order',
            )}
            value={currentSortOrder || ''}
            onChange={handleSortOrderSelectChange}
          >
            <option value="first_name">
              {text(t, 'switchboard.nodes.dialByName.firstName', 'First Name')}
            </option>
            <option value="last_name">
              {text(t, 'switchboard.nodes.dialByName.lastName', 'Last Name')}
            </option>
            <option value="first_last_name">
              {text(
                t,
                'switchboard.nodes.dialByName.firstLastName',
                'First/Last Name',
              )}
            </option>
          </NodeSelect>
        )}
      </VStack>
    </BaseNode>
  )
}

export const DialByNameNode = React.memo(
  DialByNameNodeComponent,
  nodePropsAreEqual,
)
